import { formatNumberByCountry } from 'app/commons/helpers'
import { round } from 'app/commons/helpers/compare'
import { IMoengageUserAttribute } from 'app/interface/IAuth'
import { PhoneNumberUtil } from 'google-libphonenumber'
import i18n from 'i18next'

import moment from 'moment'

const copyToClipBoard = (content: string) => {
  const textArea = document.createElement('textarea')
  textArea.value = content
  document.body.appendChild(textArea)
  textArea.select()
  document.execCommand('Copy')
  textArea.remove()
}

const formatDayHour = (milliseconds: number) => {
  const hour = Math.ceil(milliseconds / 3600 / 1000)
  if (hour < 0) return `0${i18n.t('common.short_time.day')} 0${i18n.t('common.short_time.hour')}`

  if (hour > 24) {
    if (hour % 24 === 0) {
      return `${hour / 24}${i18n.t('common.short_time.day')}`
    }
    return `${Math.floor(hour / 24)}${i18n.t('common.short_time.day')} ${hour % 24}${i18n.t('common.short_time.hour')}`
  }

  return `${hour}${i18n.t('common.short_time.hour')}`
}

const formatDateTimeUTC = (dateTime: any, shortDayFormat = true) => {
  if (!dateTime) return {}
  const month = i18n.t(`common.format_short_month.month_${moment(dateTime).month()}`)
  const day = i18n.t(`common.${shortDayFormat ? 'format_short_day' : 'format_day'}.day_${moment(dateTime).day()}`)
  return {
    date: moment(dateTime).format(`D-[${month}]-YY`),
    fullDateTime24: moment(dateTime).format(`H:mm, [${day}] D-[${month}]-YY`),
    withOutDayOfWeek: moment(dateTime).format(`H:mm, D-[${month}]-YY`),
    time24: moment(dateTime).format('H:mm'),
    dayOfWeek: day,
  }
}

const renderPricingSubText = (summaryPacked: any, isForItem: boolean, step: number, isCm3 = false) => {
  const weight = isForItem ? summaryPacked.weight / 1000 : summaryPacked.totalWeight / 1000
  const cbm = isForItem ? (isCm3 ? summaryPacked.cbm / 1000000 : summaryPacked.cbm) : summaryPacked.totalVolume

  if (weight <= 1 && cbm <= 1) {
    if (weight >= cbm) {
      if (step === 2) return i18n.t('common.information.priceWillReflect1000')
      return i18n.t('common.information.priceReflects1000')
    }
    if (weight < cbm) {
      if (step === 2) return i18n.t('common.information.priceWillReflectCBM')
      return i18n.t('common.information.priceReflectsCBM')
    }
  } else {
    if (weight === cbm) {
      if (step === 2) return i18n.t('common.information.weightPriceWillApply')
      return i18n.t('common.information.priceBasedWeight')
    }
  }

  if (weight > cbm && weight > 1) {
    if (step === 2) return i18n.t('common.information.weightPriceWillApply')
    return i18n.t('common.information.priceBasedWeight')
  }

  if (cbm > weight && cbm > 1) {
    if (step === 2) return i18n.t('common.information.CBMPriceWillApply')
    return i18n.t('common.information.priceBasedCBM')
  }

  return ''
}

const calTotalVolume = (item: { unitQuantity?: number; length?: number; width?: number; height?: number }) => {
  const { unitQuantity = 0, length = 0, width = 0, height = 0 } = item
  const timesDimensions = +length * +width * +height
  if (!timesDimensions) return 0
  const totalVolume = (+unitQuantity * timesDimensions) / Math.pow(100, 3)
  if (totalVolume < 1) {
    return totalVolume
  }
  return round(totalVolume, 2)
}

const switchCubicMeterUnit = (meter: number) => {
  const convertCentimeter = parseFloat(String(meter)) * 1000000
  const roundedMeter = parseFloat(String(meter)).toFixed(2)
  return convertCentimeter > 9999
    ? `${roundedMeter.replace('.', ',')} cbm`
    : `${formatNumberByCountry(convertCentimeter)} ${i18n.t('common.units.cm³')}`
}

const cubicMeterUnit = (meter: number) => {
  const convertCentimeter = parseFloat(String(meter)) * 1000000
  const roundedMeter = parseFloat(String(meter)).toFixed(2)
  return convertCentimeter > 9999
    ? {
        number: roundedMeter.replace('.00', ''),
        unit: 'cbm',
      }
    : {
        number: convertCentimeter.toFixed().replace('.00', ''),
        unit: i18n.t('common.units.cm³'),
      }
}
const moengageUserAttribute = (moengageUserAttribute: IMoengageUserAttribute) => {
  const companyId = moengageUserAttribute.companyID || 'N/A'
  const switchAccountId = localStorage.getItem('switch_account')
  const id = moengageUserAttribute.companyID
    ? `${moengageUserAttribute.customerId}_${moengageUserAttribute.companyID}`
    : moengageUserAttribute.customerId
  if (switchAccountId) {
    localStorage.removeItem('switch_account')
  }
  window.Moengage.add_unique_user_id(id)
  window.Moengage.add_first_name(moengageUserAttribute.firstName || 'N/A')
  window.Moengage.add_last_name(moengageUserAttribute.lastName || 'N/A')
  window.Moengage.add_user_name(moengageUserAttribute.userName || 'N/A')
  window.Moengage.add_email(moengageUserAttribute.email || 'N/A')
  window.Moengage.add_mobile(moengageUserAttribute.phone || 'N/A')
  window.Moengage.add_user_attribute('Company ID', companyId)
  window.Moengage.add_user_attribute('Country Code', moengageUserAttribute.countryCode || 'N/A')
  window.Moengage.add_user_attribute('Language', i18n.language || 'N/A')
}
const playSound = (url: string, loopCount: number) => {
  const audio = document.createElement('audio')
  audio.style.display = 'none'
  audio.src = url
  audio.autoplay = true
  let count = loopCount
  audio.onended = () => {
    count -= 1
    if (count < 1) {
      audio.remove() // Remove after played.
    } else {
      audio.play()
    }
  }
  document.body.appendChild(audio)
}

const getPhoneWithoutDialCode = (phone = '') => {
  try {
    return PhoneNumberUtil.getInstance().parse(phone).getNationalNumber()?.toString()
  } catch (error) {
    console.log('getPhoneWithoutDialCode', error)
  }
  return phone
}

const getDialCodeFromPhone = (phone = '', countryCode = '62') => {
  try {
    return PhoneNumberUtil.getInstance().parse(phone).getCountryCode()?.toString()
  } catch (error) {
    console.debug(error)
  }

  switch (countryCode) {
    case 'vn':
      return '84'
    case 'id':
      return '62'
    case 'th':
      return '66'
    case 'ph':
      return '63'
    default:
      return '62'
  }
}

const parseNumberToCountryCode = (countryCode = '62') => {
  switch (countryCode) {
    case '84':
      return 'vn'
    case '62':
      return 'id'
    case '66':
      return 'th'
    case '63':
      return 'ph'
    default:
      return 'id'
  }
}

const getPhoneNumberAndCountryCode = (phone = '', countryCode = 'id') => {
  try {
    const parsePhoneNumber = PhoneNumberUtil.getInstance().parse(phone, countryCode)
    return {
      phoneNumber: parsePhoneNumber.getNationalNumber()?.toString(),
      getCountryCode: parsePhoneNumber.getCountryCode()?.toString(),
    }
  } catch (error) {
    console.debug(error)
  }
}
export {
  copyToClipBoard,
  formatDayHour,
  formatDateTimeUTC,
  calTotalVolume,
  switchCubicMeterUnit,
  cubicMeterUnit,
  moengageUserAttribute,
  playSound,
  getPhoneWithoutDialCode,
  getDialCodeFromPhone,
  getPhoneNumberAndCountryCode,
  parseNumberToCountryCode,
  renderPricingSubText,
}
